/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Key, KeyEventType, KeyHandler } from "src/classes/plate/Keyboard";

export class KeyboardManager {
  private keysPressed: Set<string> = new Set<string>();

  private handlers: KeyHandler[];

  constructor(callbacks: KeyHandler[] = []) {
    this.handlers = callbacks;
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
  }

  /* -------------------------------------------------- */
  /*                   Public Methods                   */
  /* -------------------------------------------------- */

  public isKeyPressed(key: Key): boolean {
    return this.keysPressed.has(key);
  }

  public addKeyHandler(handler: KeyHandler): void {
    this.handlers = [...this.handlers, handler];
  }

  public removeKeyHandler(handler: KeyHandler): void {
    this.handlers = this.handlers.filter((h) => h !== handler);
  }

  /* -------------------------------------------------- */
  /*                   Private Methods                  */
  /* -------------------------------------------------- */
  private handleKeyDown = (e: KeyboardEvent) => {
    const key = e.key.toUpperCase(); // don't forget toUpperCase()!
    const handlers = this.handlers.filter((h) => h.key === key && h.type === KeyEventType.Down);
    this.executeCallbacks(handlers);
    this.keysPressed.add(key);
  };

  private handleKeyUp = (e: KeyboardEvent) => {
    const key = e.key.toUpperCase(); // don't forget toUpperCase()!
    const handlers = this.handlers.filter((h) => h.key === key && h.type === KeyEventType.Up);
    this.executeCallbacks(handlers);
    this.keysPressed.delete(key);
  };

  private executeCallbacks(handlers: KeyHandler[]): void {
    handlers.forEach((handler) => {
      if (handler.spammable) {
        handler.callback();
      } else if (handler.type === KeyEventType.Down && !this.isKeyPressed(handler.key)) {
        handler.callback();
      } else if (handler.type === KeyEventType.Up) {
        handler.callback();
      }
    });
  }

  public destroy(): void {
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
  }
}
